// Reaction configurations for avatar game-triggered animations

/**
 *
 * This file defines animated reactions that avatars perform in response to game events.
 * Each reaction includes head movement and timing parameters.
 * Facial feature transforms are handled by the expression system.
 *
 * REACTION SYSTEM:
 * - Each reaction has head transforms (whole balloon movement)
 * - All animations use the central RAF scheduler for performance
 */

export type ReactionType = 'happy1';

/**
 * Reaction animation parameters for head movement
 */
export interface ReactionParams {
  speedX?: number; // Horizontal movement speed (cycles per second);
  speedY?: number; // Vertical movement speed (cycles per second);
  sizeX?: number; // Horizontal movement distance (pixels);
  sizeY?: number; // Vertical movement distance (pixels);
  speedScale?: number; // Scale pulsing speed (cycles per second);
  sizeScale?: number; // Scale change amount (multiplier);
}

/**
 * Reaction configuration for head movement
 */
export interface ReactionConfig {
  name: string; // Human-readable name;
  duration: number; // Total duration in milliseconds;
  head: ReactionParams; // Head movement parameters;
}

/**
 * Reaction configurations
 */
export const REACTIONS: Record<ReactionType, ReactionConfig> = {
  happy1: {
    name: 'Joyful Bounce',
    duration: 2500,
    head: {
      speedX: 12, // Moderate horizontal bouncing
      speedY: 10, // Slightly slower vertical movement
      sizeX: 4, // Moderate horizontal amplitude
      sizeY: 3, // Moderate vertical amplitude
      speedScale: 6, // Gentle scale pulsing
      sizeScale: 0.05, // Subtle scale changes
    },
  },
};

/**
 * Calculate head transform at a given time
 *
 * @param reactionType - Type of reaction to calculate
 * @param time - Current animation time in seconds
 * @returns Transform values for head only
 */
export function calculateReactionTransform(
  reactionType: ReactionType,
  time: number
): {
  head: { x: number; y: number; rotation: number; scale: number };
} {
  const config = REACTIONS[reactionType];
  if (!config) {
    return {
      head: { x: 0, y: 0, rotation: 0, scale: 1 },
    };
  }

  const head = calculateHeadTransform(config.head, time);
  return {
    head: {
      x: head.shiftX,
      y: head.shiftY,
      rotation: head.rotation,
      scale: head.scale,
    },
  };
}

/**
 * Calculate head transform from parameters
 */
function calculateHeadTransform(
  params: ReactionParams,
  time: number
): { shiftX: number; shiftY: number; scale: number; rotation: number } {
  let shiftX = 0;
  let shiftY = 0;
  let scale = 1;
  const rotation = 0;

  // Calculate X movement using sine wave
  if (params.speedX && params.sizeX) {
    shiftX = Math.sin(time * params.speedX) * params.sizeX;
  }

  // Calculate Y movement using sine wave
  if (params.speedY && params.sizeY) {
    shiftY = Math.sin(time * params.speedY) * params.sizeY;
  }

  // Calculate scale changes
  if (params.speedScale && params.sizeScale) {
    scale += Math.sin(time * params.speedScale) * params.sizeScale;
  }

  return { shiftX, shiftY, scale, rotation };
}

/**
 * Check if a reaction type is valid
 */
export function isValidReactionType(
  reactionType: string
): reactionType is ReactionType {
  return Object.keys(REACTIONS).includes(reactionType);
}
